import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable ,  Subject } from 'rxjs';
import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { Option } from 'shared/form/select/option.interface';
import { createOption, defaultOption } from 'shared/form/select/option-helpers';

import { BaseSegmentComponent } from '../base/_base-segment.component';
import { UserAttributesComponent } from './user-attributes/user-attributes.component';
import { SuggestedAttributesComponent } from './suggested-attributes/suggested-attributes.component';
import { ProductPanelService } from '../../product-panel.service';
import { segmentsData } from '../../../product-details/segments-data-stub';
import { AttributesService } from './attributes.service';
import { SearchService } from 'shared/search/search.service';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Component({
  moduleId: module.id,
  selector: 'attributes-segment',
  templateUrl: 'attributes.component.html',
  styleUrls: ['attributes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributesComponent extends BaseSegmentComponent implements OnInit {

  @ViewChild(UserAttributesComponent, {static: false}) userAttributes: UserAttributesComponent | void;

  @ViewChild(SuggestedAttributesComponent, {static: false}) suggestedAttributes: SuggestedAttributesComponent | void;

  unitsData: Array<Option> = [
    {
      option: '',
      title: '',
    },
    {
      option: 'mm',
      title: 'mm',
    },
    {
      option: 'in',
      title: 'in',
    }
  ];

  userUnitsData: Array<Option> = [];

  itemDetailsData: Array<any> = [];
  userHeaders: Array<any>;
  userFields: Array<any>;
  segmentName: string = 'attributes';
  hasPartType: boolean;

  private subData = new Subject<any>();
  private obsData: Observable<any>;

  constructor(
    protected service: AttributesService,
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  get userData() {
    return this.itemDetailsData;
  }

  get segmentForm(): UntypedFormGroup {
    return this.userAttributes && this.userAttributes.attributesFormGroup;
  }

  get fields() {
    return segmentsData.attributes;
  }

  getData(): Observable<any> {
    return this.subData.asObservable();
  }

  ngOnInit() {
    this.initFields();

    this.updateUom();

    /* Subscribe to the itemDetail */
    this.subscribeToItemDetail();

  }

  // replace parent method:
  setBulkData() {
    this.hasPartType = true;
    this.setData(this.getBulkSegmentData(this.segmentName));
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.hasPartType = itemDetail.partTerminology && itemDetail.partTerminology.id ?
      true :
      false;
    if (itemDetail.attributes) {
      // find PAdb attributes and create requests for them
      const requestArray: any[] = [];
      const padbFilters: any[] = [];
      _.forEach(itemDetail.attributes, (item: any) => {
        if (item.is_padb === 'Y') {
          requestArray.push(this.service.getPadbAttributeDetails(item.assignment_id));
        }
      });
      // if we need to request additional data for PAdb attributes -
      // request and then set data
      if (requestArray.length) {
        forkJoin(requestArray)
        .subscribe(results => {
          _.forEach(results, (result: any) => {
            let item = _.find(itemDetail.attributes, {assignment_id: result.assignment_id});
            if (item) {
              item = Object.assign(item, result);
            }
          });
          this.setData(itemDetail.attributes);
        });
      // if not - set data
      } else {
        this.setData(itemDetail.attributes);
      }
    }
    if (itemDetail.itemId && itemDetail.partTerminology) {
      if (this.suggestedAttributes instanceof SuggestedAttributesComponent) {
        this.suggestedAttributes.setData(
          itemDetail.itemId,
          itemDetail.partTerminology.id
        );
      }
    }
  }

  setData(itemDetail: any = []) {
    this.itemDetailsData = itemDetail;
    this.subData.next(this.itemDetailsData);
    this.ref.markForCheck();
  }

  getFormData(): any {
    return _.map(this.userAttributes && this.userAttributes.attributes.getRawValue() || [], (attribute: any) => {

      // an attribute should have next fields:
      //
      // attribute_id - string
      // is_padb - "Y"/"N"
      // uom - string or ""
      // value - string or array
      // style_id? - number
      // assignment_id? - number

      const attributeToSave: any = {
        attribute_id: attribute.is_padb === 'Y' ? attribute.attribute_id : attribute.name,
        is_padb: attribute.is_padb ? attribute.is_padb : 'N',
        uom: attribute.uom ? attribute.uom : '',
        value: (attribute.is_padb === 'N' && Array.isArray(attribute.value)) ? attribute.value[0] : attribute.value,
      };

      // handle assignment_id
      if (attribute.assignment_id) {
        attributeToSave.assignment_id = attribute.assignment_id;
      }

      // handle style
      if (attribute.style_id) {
        attributeToSave.style_id = attribute.style_id;
      }
      // save data for the bulk edit mode
      if (this.bulkEdit) {
        attributeToSave.name = attribute.name;
        attributeToSave.uom_description = attribute.uom_description;
      }
      return attributeToSave;
    });
  }

  save(): Observable<any> {
    const attributes: Array<any> = this.getFormData();
    if (this.bulkEdit) {
      return this.putDataBulk(this.segmentName, attributes);
    } else {
      const { id } = this.route.snapshot.params;
      return this.putData(id, this.segmentName, attributes);
    }
  }

  initFields() {
    this.userHeaders = _.filter(segmentsData.attributes, (item: any) => {
      switch (item.item_name) {
        case 'name':
        case 'value':
        case 'uom_description':
        case 'style_id':
          return true;
        default:
          return false;
      }
    });

    this.userFields = _.filter(segmentsData.attributes, (item: any) => {
      switch (item.item_name) {
        case 'name':
        case 'value':
        case 'uom_description':
        case 'uom':
        // only for PAdb
        // falls through
        case 'attribute_id':
        case 'style_id':
        case 'is_padb':
        case 'assignment_id':
          return true;
        default:
          return false;
      }
    });
  }
  // handlers
  onAddSuggestedAttributes(attributes: Array<any>) {
    _.forEach(attributes, attribute => {
      attribute.is_padb = 'Y';
    });
    if (this.userAttributes instanceof UserAttributesComponent) {
      this.userAttributes.addAttributes(attributes, true);
    }
    this.ref.markForCheck();
  }

  onRemoveRows(attributesIds: any[]) {
    if (!this.bulkEdit) {
      if (this.suggestedAttributes instanceof SuggestedAttributesComponent) {
        this.suggestedAttributes.removeFilters(attributesIds);
      }
    }
  }

  onSaved() {
    if (this.suggestedAttributes instanceof SuggestedAttributesComponent) {
      this.suggestedAttributes.clearFilters();
    }
  }

  // private
  private updateUom() {
    this.service.getUom()
    .subscribe(data => {
      this.unitsData = data;
      this.userUnitsData = data;
      this.userUnitsData.splice(0, 0, defaultOption);
      if (this.bulkEdit) {
        this.addLookupPlaceholder(this.unitsData);
      }
    });
  }

}
